<template>
  <div class="app-menu-desktop__nav-menu">
    <ul class="scrollbar">
      <li
        v-for="item in menu"
        :key="item.id"
        :class="{ active: activeId === item.id, 'has-children': item.children }"
        @mouseover="$emit('setLevel', item)"
      >
        <NuxtLink
          :to="item.url"
          :external="item.external"
          :target="item.external ? '_blank' : undefined"
          :rel="item.external ? 'noopener noreferrer' : undefined"
          class="text-secondary-nav"
        >
          {{ item.title }}

          <IconChevronRight v-if="item.children" class="icon-arrow" />
        </NuxtLink>
      </li>
    </ul>

    <div>
      <NuxtLink
        v-if="allLink"
        :to="allLink.url"
        :external="allLink.external"
        :target="allLink.external ? '_blank' : undefined"
        :rel="allLink.external ? 'noopener noreferrer' : undefined"
        class="text-red hover:text-tint-red-hover text-xs"
      >
        {{ $t('string.Show-all') }} {{ allLink.title }}
      </NuxtLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { MenuItem } from './AppMenuDesktopNav.vue'

type Props = {
  menu: MenuItem[]
  activeId?: string
  allLink?: {
    title: string
    url: string
    external: boolean
  }
}

defineProps<Props>()

defineEmits<{
  (e: 'setLevel', item?: MenuItem): void
}>()
</script>

<style lang="scss">
@import '@boats/ui/styles/config';

.desktop-nav-width-enter-active,
.desktop-nav-width-leave-active {
  @include transition('width');
}

.desktop-nav-width-enter-from,
.desktop-nav-width-leave-to {
  width: 0 !important;
}

.app-menu-desktop__nav-menu {
  $width: 15.75rem;
  $padding-x: 1.5rem;

  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: $width;
  margin-left: -0.125rem;
  border-left: 1px solid $gray-200;
  overflow: hidden;
  box-sizing: content-box;

  &:first-child {
    margin-left: 0;
    border-left: 0;
  }

  div {
    display: flex;
    width: $width;
    margin-top: auto;
    padding: 0 $padding-x;
  }

  ul {
    width: $width;
    margin: 0 0 1.75rem;
    padding: 0 $padding-x;
    list-style: none;
    max-height: 50vh;
    overflow-y: auto;

    &:last-child {
      margin-bottom: 0;
    }

    li {
      margin-bottom: 0.5rem;

      &:last-child {
        margin-bottom: 0;
      }

      a {
        position: relative;
        display: flex;
        justify-content: space-between;
        align-items: center;
        min-height: 2.25rem;
        padding: 0.375rem 0.625rem;
        line-height: 1.25;
        text-decoration: none !important;
        border-radius: 0.125rem;
        @include transition('background-color');
      }

      &:hover {
        & > a {
          background-color: $gray-100;
        }
      }

      &.active {
        background-color: $gray-100;
      }

      &.has-children {
        & > a {
          padding-right: 1.75rem;
        }
      }
    }

    .icon-arrow {
      position: absolute;
      top: 50%;
      right: 0.25rem;
      width: 1.25rem;
      height: 1.25rem;
      transform: translateY(-50%);
    }
  }
}
</style>
